/**
 * CommandPalette · Sprint 10 · Operational Excellence.
 *
 * PO Phase-1 scope (2026-08-05):
 *   ✅ Workspace navigation
 *   ✅ Quick actions
 *   ⏳ Entity search / recent records — deferred
 *
 * Global keyboard shortcuts wired here:
 *   ⌘K / Ctrl+K   Toggle palette
 *   N             New Order       (from anywhere)
 *   /             Focus search    (page-local — palette opens)
 *   Esc           Close palette
 *
 * The palette is presentation-only — every action either navigates
 * or delegates to an existing endpoint. No business logic here.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList,
} from "@/components/ui/command";
import {
  House, ClipboardText, Truck, Package, ChartBar, PhoneCall,
  Storefront, CurrencyCircleDollar, FileText, ChatCircle, Sparkle,
  Plus, UploadSimple, Users, Envelope, Robot,
} from "@phosphor-icons/react";

const WORKSPACES = [
  { key: "dashboard",           label: "Dashboard",              to: "/dashboard",              icon: House,                keywords: "home landing" },
  { key: "morning-brief",       label: "Morning Brief",          to: "/brief",                  icon: ChartBar,             keywords: "brief executive summary" },
  { key: "orders",              label: "Orders",                 to: "/orders",                 icon: ClipboardText,        keywords: "order fuel delivery" },
  { key: "receiving",           label: "Receiving",              to: "/receiving",              icon: FileText,             keywords: "invoice supplier suncor" },
  { key: "freight-exceptions",  label: "Freight Exceptions",     to: "/receiving/freight-exceptions", icon: Truck,        keywords: "freight aaray exception" },
  { key: "freight-outstanding", label: "Freight Outstanding",    to: "/receiving/freight-outstanding", icon: Truck,       keywords: "freight outstanding awaiting aging provisional landed" },
  { key: "fleet",               label: "Fleet",                  to: "/fleet",                  icon: Truck,                keywords: "trucks vehicles" },
  { key: "daily-pricing",       label: "Daily Pricing",          to: "/price-notifications",    icon: CurrencyCircleDollar, keywords: "price notification suncor pricing" },
  { key: "operator-metrics",    label: "Operator Effectiveness", to: "/ops-metrics",            icon: Sparkle,              keywords: "metrics automation dashboard" },
  { key: "parser-health",       label: "Parser Health",          to: "/admin/parser-health",    icon: Robot,                keywords: "parser diagnostics admin health" },
  { key: "customers",           label: "Customers",              to: "/customers",              icon: Users,                keywords: "clients accounts" },
  { key: "phone-messages",      label: "Phone Messages",         to: "/messages",               icon: PhoneCall,            keywords: "calls messages" },
];

export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  // Global keyboard listener
  useEffect(() => {
    const onKey = (e) => {
      // ⌘K or Ctrl+K — toggle palette
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Only listen for un-modified single-key shortcuts when the user
      // isn't typing in a text field.
      const target = e.target;
      const isTyping = target && (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      );
      if (isTyping || e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "/") {
        // Open the palette as the universal search entry-point.
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        nav("/new-order");
      } else if (e.key === "?") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  const go = useCallback((to) => { setOpen(false); nav(to); }, [nav]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div data-testid="command-palette">
        <CommandInput
          placeholder="Jump to a workspace or run a quick action…"
          data-testid="command-palette-input"
        />
        <CommandList>
          <CommandEmpty>Nothing matches. Press Esc to close.</CommandEmpty>

          <CommandGroup heading="Quick actions">
            <CommandItem
              value="new order create"
              onSelect={() => go("/new-order")}
              data-testid="cmd-quick-new-order"
            >
              <Plus size={14} className="mr-2" /> New Order
              <span className="ml-auto text-[10px] text-slate-400">N</span>
            </CommandItem>
            <CommandItem
              value="new phone message call"
              onSelect={() => go("/messages")}
              data-testid="cmd-quick-new-phone-message"
            >
              <PhoneCall size={14} className="mr-2" /> New Phone Message
            </CommandItem>
            <CommandItem
              value="upload supplier invoice"
              onSelect={() => go("/receiving")}
              data-testid="cmd-quick-upload-supplier"
            >
              <UploadSimple size={14} className="mr-2" /> Upload Supplier Invoice
            </CommandItem>
            <CommandItem
              value="upload freight invoice"
              onSelect={() => go("/receiving/freight-exceptions")}
              data-testid="cmd-quick-upload-freight"
            >
              <Truck size={14} className="mr-2" /> Upload Freight Invoice
            </CommandItem>
          </CommandGroup>

          <CommandGroup heading="Go to workspace">
            {WORKSPACES.map((w) => {
              const Icon = w.icon;
              return (
                <CommandItem
                  key={w.key}
                  value={`${w.label} ${w.keywords}`}
                  onSelect={() => go(w.to)}
                  data-testid={`cmd-goto-${w.key}`}
                >
                  <Icon size={14} className="mr-2" /> {w.label}
                </CommandItem>
              );
            })}
          </CommandGroup>

          <CommandGroup heading="Help">
            <CommandItem value="keyboard shortcuts help" onSelect={() => {}}>
              <ChatCircle size={14} className="mr-2" />
              Keyboard: ⌘K palette · N new order · / palette · Esc close
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </div>
    </CommandDialog>
  );
}
